import useTheme from "../../../../../common/hooks/useTheme";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../../store/platform-slice";
import CircularGraph from "../../common/CircularGraph";
import ScoreMessageInfoPanel from "../ScoreMessageInfoPanel";
import React from "react";
import { getTimeValue, PointerChartType } from "../../../store/helper";
import ItemCardHome from "../ItemCardHome";
import BreakdownBarChart from "../BreakdownBarChart";
import PointerChart from "../PointerChart";
import { format } from "date-fns";
import { Box } from "@mui/material";
import { useAppStore } from "../../../../../providers/AppStore";
import BlurContainer from "../../../../../common/ui/BlurContainer";
import { useAuth } from "../../../../../providers/AuthProvider";

const RecoveryTab = () => {
  const theme = useTheme();
  const { ASIsIntegrationActive } = useAppStore();
  const { AuthUser, AuthIsSimpleUser } = useAuth();
  const { ASSelectedClient } = useAppStore();

  const ASActiveDate = useSelector(
    (state: RootStatePlatform) => state.platform.ASActiveDate
  );

  const homeData = useSelector(
    (state: RootStatePlatform) => state.platform.dataNutServer.recovery_tab
  );

  const getRecoveryColorCodingType = (score: number) => {
    if (score === 0) {
      return 3;
    } else if (score < 60) {
      return 5;
    } else if (score >= 90) {
      return 1;
    } else {
      return 2;
    }
  };

  const sleepZonesData = [
    {
      index: 0,
      label: "Awake",
      value: Math.round(homeData.awake_duration_in_minutes),
    },
    {
      index: 1,
      label: "Core",
      value: Math.round(homeData.light_duration_in_minutes),
    },

    {
      index: 2,
      label: "Deep",
      value: Math.round(homeData.deep_duration_in_minutes),
    },
    {
      index: 3,
      label: "REM",
      value: Math.round(homeData.rem_duration_in_minutes),
    },
  ];

  const totalSleepTime = sleepZonesData.reduce(
    (sum: number, el: any) => sum + el.value,
    0
  );

  const sleepBreakdownData = () => {
    const percentage1 = Math.round(
      (sleepZonesData[0].value * 100) / totalSleepTime
    );

    const percentage2 = Math.round(
      (sleepZonesData[1].value * 100) / totalSleepTime
    );

    const percentage3 = Math.round(
      (sleepZonesData[2].value * 100) / totalSleepTime
    );

    return sleepZonesData.map((item: any) => {
      return {
        index: item.index,
        label: item.label,
        value: getTimeValue(item.value),
        unit: "",
        percentage:
          item.index === 4
            ? 100 - percentage1 - percentage2 - percentage3 + "%"
            : Math.round((item.value * 100) / totalSleepTime) + "%",
      };
    });
  };

  const getSubtitleText = () => {
    if (ASIsIntegrationActive()) {
      if (
        AuthIsSimpleUser
          ? AuthUser.has_advanced_app_access
          : ASSelectedClient?.has_advanced_app_access
      ) {
        return "";
      }
    } else {
      return AuthIsSimpleUser
        ? "And gain general activities insights"
        : "Please advise your client to connect their wearable to gain insights into recovery section";
    }
    return "";
  };

  const getTitleText = () => {
    if (ASIsIntegrationActive()) {
      if (
        AuthIsSimpleUser
          ? !AuthUser.has_advanced_app_access
          : !ASSelectedClient?.has_advanced_app_access
      ) {
        return "Please contact your PNOE tester\n to get started";
      }
    } else {
      return AuthIsSimpleUser
        ? "Connect your wearable"
        : "No wearable connected";
    }
    return "";
  };

  return (
    <div style={styles.container()}>
      <CircularGraph
        size={121}
        strokeWidth={6}
        value={homeData.score}
        color={theme.colors_ds.PRIMARY_ICON_MAIN}
        backgroundColor={theme.colorTokens.P[10]}
        label={"Recovery"}
        isMainChart={true}
        initialValue={homeData.initial_score}
      />

      <BlurContainer
        isBlurred={
          !ASIsIntegrationActive() ||
          (AuthIsSimpleUser
            ? !AuthUser.has_advanced_app_access
            : !ASSelectedClient?.has_advanced_app_access)
        }
        title={getTitleText()}
        text={getSubtitleText()}
        customBorderColor={
          (
            AuthIsSimpleUser
              ? !AuthUser.has_advanced_app_access
              : !ASSelectedClient?.has_advanced_app_access
          )
            ? theme.colorTokens.W[60]
            : undefined
        }
      >
        {AuthIsSimpleUser &&
        ASActiveDate === format(new Date().toISOString(), "yyyy-MM-dd") ? (
          <ScoreMessageInfoPanel
            data={homeData.score_message}
            colorCodingType={getRecoveryColorCodingType(homeData.score)}
          />
        ) : (
          <Box style={{ height: "24px" }} />
        )}
        <PointerChart
          title="Recovery (%)"
          lowLimit={homeData.min_score}
          highLimit={homeData.max_score}
          value={homeData.score}
          initialValue={homeData.initial_score}
          rangeMessage={"Day range"}
          type={PointerChartType.recovery}
        />
        <PointerChart
          title="Heart rate variability (SDNN)"
          lowLimit={homeData.hrv_bad_low}
          highLimit={homeData.hrv_good_high}
          value={homeData.hrv_avg !== 0 ? homeData.hrv_avg : -1}
          rangeMessage={"30 day range"}
          type={PointerChartType.initialValues}
        />
        <div style={styles.title(theme)}>Sleep data</div>
        <div style={styles.text(theme)}>
          Track sleep data by wearing a fitness tracker overnight.
        </div>
        <div style={styles.cardsContainer}>
          <ItemCardHome
            label={"Sleep time"}
            value={homeData.sleep_duration.current}
            maxValue={homeData.sleep_duration.target}
            hasBoldLabel={true}
            unit="hr"
          />
          <ItemCardHome
            label={"Sleep quality"}
            value={homeData.sleep_quality}
            maxValue={100}
            showPercentage={true}
            unit="%"
          />
        </div>
        {totalSleepTime !== 0 && (
          <BreakdownBarChart
            data={sleepBreakdownData()}
            type="withPercentageAndTime"
          />
        )}
        <PointerChart
          title="Resting heart rate (bpm)"
          lowLimit={homeData.rhr_good_low}
          highLimit={homeData.rhr_bad_high}
          value={homeData.rhr_avg !== 0 ? homeData.rhr_avg : -1}
          rangeMessage={"30 day range"}
          type={PointerChartType.reverseValues}
        />
      </BlurContainer>
    </div>
  );
};

export default RecoveryTab;

const styles = {
  container: () => ({
    width: "100%",
    paddingTop: "32px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  }),
  title: (theme: any) => ({
    marginTop: "32px",
    alignSelf: "flex-start",
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  text: (theme: any) => ({
    marginBottom: "8px",
    alignSelf: "flex-start",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  cardsContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
};
